import { useEffect, useRef } from 'react';
import { Entity } from '../types/simulation';

export function useKeyboardMovement(
  myEntity: Entity | null | undefined,
  onPositionChange: (position: number[]) => void,
  cameraRotation?: { yaw: number; pitch: number }
) {
  const keysPressed = useRef<Set<string>>(new Set());
  const speed = 5; // units per second
  const lastUpdateRef = useRef<number>(Date.now());
  const cameraRotationRef = useRef(cameraRotation);
  const myEntityRef = useRef(myEntity);
  const onPositionChangeRef = useRef(onPositionChange);

  // Update refs when values change
  useEffect(() => {
    cameraRotationRef.current = cameraRotation;
  }, [cameraRotation]);

  useEffect(() => {
    myEntityRef.current = myEntity;
  }, [myEntity]);

  useEffect(() => {
    onPositionChangeRef.current = onPositionChange;
  }, [onPositionChange]);

  useEffect(() => {
    // Only set up keyboard handlers if we have an entity
    if (!myEntity) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't handle if user is typing in an input
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
        return;
      }

      const key = e.key.toLowerCase();
      if (['w', 'a', 's', 'd', 'e', 'x'].includes(key)) {
        e.preventDefault();
        keysPressed.current.add(key);
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      const key = e.key.toLowerCase();
      keysPressed.current.delete(key);
    };

    // Movement loop using delta time
    let animationId: number;
    const updateMovement = () => {
      const now = Date.now();
      const deltaTime = (now - lastUpdateRef.current) / 1000; // seconds
      lastUpdateRef.current = now;

      const entity = myEntityRef.current;
      if (keysPressed.current.size > 0 && entity) {
        const currentPos = [entity.position.x, entity.position.y, entity.position.z];
        let moved = false;

        // Calculate movement direction based on camera rotation
        const yaw = cameraRotationRef.current?.yaw ?? 0;
        const forward = { x: -Math.sin(yaw), z: -Math.cos(yaw) };
        const right = { x: Math.cos(yaw), z: -Math.sin(yaw) };

        if (keysPressed.current.has('w')) {
          currentPos[0] += forward.x * speed * deltaTime;
          currentPos[2] += forward.z * speed * deltaTime;
          moved = true;
        }
        if (keysPressed.current.has('s')) {
          currentPos[0] -= forward.x * speed * deltaTime;
          currentPos[2] -= forward.z * speed * deltaTime;
          moved = true;
        }
        if (keysPressed.current.has('a')) {
          currentPos[0] -= right.x * speed * deltaTime;
          currentPos[2] -= right.z * speed * deltaTime;
          moved = true;
        }
        if (keysPressed.current.has('d')) {
          currentPos[0] += right.x * speed * deltaTime;
          currentPos[2] += right.z * speed * deltaTime;
          moved = true;
        }
        if (keysPressed.current.has('e')) {
          currentPos[1] += speed * deltaTime;
          moved = true;
        }
        if (keysPressed.current.has('x')) {
          currentPos[1] -= speed * deltaTime;
          moved = true;
        }

        if (moved) {
          onPositionChangeRef.current(currentPos);
        }
      }

      animationId = requestAnimationFrame(updateMovement);
    };

    updateMovement();

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      cancelAnimationFrame(animationId);
    };
  }, [myEntity ? myEntity.id : null]);
}
